import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProBadgeSmall } from '../components/ProBadge';
import BannerAd from '../components/BannerAd';
import { API_BASE_URL } from '../config/runtime';
import { categoryLabel } from '../i18n/categoryNames';
import { Star } from 'lucide-react';
import './ServicesPage.css';

interface Service {
  id: number;
  title: string;
  description: string;
  short_description?: string;
  price_basic: number;
  seller_nickname: string;
  seller_tier?: string;
  is_pro?: boolean;
  category_name: string;
  category_icon?: string;
  rating_average: number;
}

interface Category {
  name: string;
  icon: string;
}

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [sortFilter, setSortFilter] = useState('created_at-DESC');
  const [daysFilter, setDaysFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  const loadCategories = () => {
    setCategories([
      { name: '웹 개발', icon: '' },
      { name: '모바일 앱', icon: '' },
      { name: '디자인', icon: '' },
      { name: '마케팅', icon: '' },
      { name: '글쓰기', icon: '' },
      { name: '번역', icon: '' },
      { name: '영상 편집', icon: '' },
      { name: '기타', icon: '' }
    ]);
  };

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/services?page=${currentPage}&limit=12`;

      if (searchInput) url += `&search=${encodeURIComponent(searchInput)}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      if (priceFilter) {
        const [min, max] = priceFilter.split('-');
        url += `&min_price=${min}&max_price=${max}`;
      }
      if (sortFilter) {
        const [sort, order] = sortFilter.split('-');
        url += `&sort=${sort}&order=${order}`;
      }
      if (daysFilter) url += `&max_days=${daysFilter}`;

      console.log('[ServicesPage] 서비스 조회 URL:', url);
      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        setServices(result.data);
        if (result.pagination) setTotalPages(result.pagination.totalPages);
        console.log('[ServicesPage] 서비스 로드 완료:', result.data.length, '개');
      }
    } catch (error) {
      console.error('[ServicesPage] 서비스 로드 오류:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, categoryFilter, priceFilter, sortFilter, searchInput, daysFilter]);

  useEffect(() => {
    loadCategories();
    loadServices();
  }, [loadServices]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadServices();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const viewService = (serviceId: number) => navigate(`/services/${serviceId}`);
  const viewFreelancer = (nickname: string) => navigate(`/freelancer/${nickname}`);
  const formatPrice = (price: number) => new Intl.NumberFormat('ko-KR').format(price);

  return (
    <div className="services-page">
      {/* 상단 배너 광고 (무료 사용자만) */}
      <BannerAd position="top" />

      <main className="services-main">
        <div className="container">
          <div className="page-header">
            <h1>{t('services.title')}</h1>
            <p>{t('services.subtitle')}</p>
          </div>

          <div className="search-section">
            <div className="search-form">
              <input
                type="text"
                className="search-input"
                placeholder={t('services.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="">{t('services.allCategories')}</option>
                {categories.map(c => <option key={c.name} value={c.name}>{categoryLabel(c.name)}</option>)}
              </select>
              <select className="filter-select" value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}>
                <option value="">{t('services.allPrices')}</option>
                <option value="0-100000">{t('services.priceUnder100k')}</option>
                <option value="100000-500000">{t('services.price100to500')}</option>
                <option value="500000-1000000">{t('services.price500to1m')}</option>
                <option value="1000000-999999999">{t('services.priceOver1m')}</option>
              </select>
              <select className="filter-select" value={daysFilter} onChange={(e) => { console.log('[ServicesPage] 납기일 필터:', e.target.value); setDaysFilter(e.target.value); }}>
                <option value="">{t('services.anyDeadline')}</option>
                <option value="3">{t('services.within', {n:3})}</option>
                <option value="7">{t('services.within', {n:7})}</option>
                <option value="14">{t('services.within', {n:14})}</option>
                <option value="30">{t('services.within', {n:30})}</option>
              </select>
              <select className="filter-select" value={sortFilter} onChange={(e) => setSortFilter(e.target.value)}>
                <option value="created_at-DESC">{t('services.sortNewest')}</option>
                <option value="price_basic-ASC">{t('services.sortPriceLow')}</option>
                <option value="price_basic-DESC">{t('services.sortPriceHigh')}</option>
                <option value="rating_average-DESC">{t('services.sortRating')}</option>
                <option value="views_count-DESC">{t('services.sortPopular')}</option>
              </select>
              <button className="btn btn-primary" onClick={handleSearch}>{t('common.search')}</button>
            </div>
          </div>

          <div className="services-container">
            {loading ? (
              <div className="loading">서비스를 불러오는 중입니다...</div>
            ) : services.length === 0 ? (
              <div className="empty-state">
                <h3>{t('services.noResults')}</h3>
                <p>{t('services.noResultsDesc')}</p>
              </div>
            ) : (
              <>
                <div className="services-grid">
                  {services.map((service, index) => {
                    const sellerInitial = service.seller_nickname?.charAt(0).toUpperCase() || 'F';

                    return (
                      <React.Fragment key={service.id}>
                        <div 
                          className="service-card" 
                          onClick={() => viewService(service.id)}
                          style={{ position: 'relative' }}
                        >
                          {/* Pro 뱃지 */}
                          {service.is_pro && (
                            <div style={{
                              position: 'absolute', top: 10, right: 10,
                              display: 'flex', alignItems: 'center', gap: 3,
                              padding: '3px 8px',
                              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                              color: '#fff', fontSize: 11, fontWeight: 700,
                              borderRadius: 6, zIndex: 1, letterSpacing: 0.3,
                            }}>
                              <Star size={10} fill="white" /> PRO
                            </div>
                          )}

                          <div className="service-image">
                            {categoryLabel(service.category_name || '기타')}
                          </div>
                          <div className="service-content">
                            <div className="service-category">{categoryLabel(service.category_name || '기타')}</div>
                            <div className="service-title">{service.title}</div>
                            <div className="service-freelancer">
                              <div className="freelancer-avatar">{sellerInitial}</div>
                              <span 
                                onClick={(e) => { e.stopPropagation(); viewFreelancer(service.seller_nickname); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                              >
                                {service.seller_nickname || '전문가'}
                                <ProBadgeSmall isPro={service.is_pro || false} />
                              </span>
                            </div>
                            <div className="service-description">
                              {service.short_description || service.description}
                            </div>
                            <div className="service-footer">
                              <div className="service-price">{formatPrice(service.price_basic)}원~</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {(service as any).delivery_days_basic && (
                                  <span style={{ fontSize: 12, color: '#AAA' }}>
                                    {(service as any).delivery_days_basic}일
                                  </span>
                                )}
                                <div className="service-rating">
                                  <span className="rating-stars">{'★'.repeat(Math.round(service.rating_average || 0))}</span>
                                  {service.rating_average > 0 && <span>({service.rating_average})</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 4개마다 인라인 광고 삽입 (무료 사용자만) */}
                        {(index + 1) % 4 === 0 && index !== services.length - 1 && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <BannerAd position="inline" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>이전</button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => (
                <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={currentPage === i + 1 ? 'current' : ''}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>다음</button>
            </div>
          )}

          {/* 하단 배너 광고 (무료 사용자만) */}
          <div style={{ marginTop: '30px' }}>
            <BannerAd position="bottom" />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServicesPage;
